import type {AccountLifecycle, ConsultationOrders, DialogDirectory, UserProfile, WalletView} from "./ports";
import {sessionStore} from "../../shared/session/session-store";
import {
  UnavailableAccountLifecycle,
  UnavailableConsultationOrders,
  UnavailableWalletView,
} from "./unavailable-ports";

export const cabinetFixturesEnabled = import.meta.env.VITE_CABINET_FIXTURES === "1";

let profilePort: Promise<UserProfile> | undefined;
let profileEpoch: number | undefined;
let walletPort: Promise<WalletView> | undefined;
let ordersPort: Promise<ConsultationOrders> | undefined;
let dialogsPort: Promise<DialogDirectory> | undefined;
let accountPort: Promise<AccountLifecycle> | undefined;
let accountEpoch: number | undefined;

export function getUserProfilePort(): Promise<UserProfile> {
  if (!cabinetFixturesEnabled) {
    const owner = sessionStore.captureOwner();
    if (profileEpoch !== owner.epoch) {
      profileEpoch = owner.epoch;
      profilePort = import("./api-profile").then(({ApiUserProfile}) => new ApiUserProfile(owner));
    }
    return profilePort!;
  }
  profilePort ??= cabinetFixturesEnabled
    ? import("./fixtures/profile").then(({FixtureUserProfile}) => new FixtureUserProfile())
    : import("./api-profile").then(({ApiUserProfile}) => new ApiUserProfile());
  return profilePort;
}

export function getWalletViewPort(): Promise<WalletView> {
  walletPort ??= cabinetFixturesEnabled
    ? import("./fixtures/wallet").then(({FixtureWalletView}) => new FixtureWalletView())
    : Promise.resolve(new UnavailableWalletView());
  return walletPort;
}

export function getConsultationOrdersPort(): Promise<ConsultationOrders> {
  ordersPort ??= cabinetFixturesEnabled
    ? import("./fixtures/orders").then(({FixtureConsultationOrders}) => new FixtureConsultationOrders())
    : Promise.resolve(new UnavailableConsultationOrders());
  return ordersPort;
}

export function getDialogDirectoryPort(): Promise<DialogDirectory> {
  dialogsPort ??= cabinetFixturesEnabled
    ? import("./fixtures/dialogs").then(({FixtureDialogDirectory}) => new FixtureDialogDirectory())
    : import("../dialogs/api").then(({ApiDialogDirectory}) => new ApiDialogDirectory());
  return dialogsPort;
}

export function getAccountLifecyclePort(): Promise<AccountLifecycle> {
  if (!cabinetFixturesEnabled) {
    const owner = sessionStore.captureOwner();
    if (accountEpoch !== owner.epoch) {
      accountEpoch = owner.epoch;
      accountPort = import("./api-account-deletion").then(({ApiAccountLifecycle}) => new ApiAccountLifecycle(owner));
    }
    return accountPort!;
  }
  accountPort ??= cabinetFixturesEnabled
    ? import("./fixtures/account").then(({FixtureAccountLifecycle}) => new FixtureAccountLifecycle())
    : Promise.resolve(new UnavailableAccountLifecycle());
  return accountPort;
}
